import React, { useState, useCallback } from 'react';
import LiveAudioRecorder from './LiveAudioRecorder';
import LiveVideoRecorder from './LiveVideoRecorder';
import FileUploader from './FileUploader';
import AnalysisReport from './AnalysisReport';
import useBackgroundTranscription, { transcriptionTimeoutMs } from '../hooks/useBackgroundTranscription';
import { probeDuration } from '../utils/media';
import { catalystFetch } from '../services/catalystFetch';

const LOADING_STAGES = [
  'Uploading audio…',
  'Transcribing with Sarvam AI…',
  'Analyzing your speech…',
];

const TABS = [
  { id: 'audio',  label: '🎤 Record Audio' },
  // { id: 'video',  label: '📹 Record Video' }, // Post-MVP
  { id: 'upload', label: '📁 Upload File'  },
  { id: 'url',    label: '🔗 Paste URL'    },
];

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/server/processMedia'
  : '';
const API_ENDPOINT = `${API_BASE}/process`;

async function postFormData(formData) {
  const res = await catalystFetch(API_ENDPOINT, { method: 'POST', body: formData });

  if (!res.ok) {
    let errorCode = 'UNKNOWN_ERROR';
    let json = {};
    try {
      json = await res.json();
      errorCode = json.error || 'UNKNOWN_ERROR';
    } catch (parseErr) {
      console.error('[UnifiedInputHub] Failed to parse error response:', parseErr);
      throw new Error(`Server error (${res.status}). Please ensure the backend is running.`);
    }

    // Soft usage limit reached — surface the cap and reset date instead of a generic error.
    if (errorCode === 'QUOTA_EXCEEDED') {
      const caps = json.caps || {};
      const resetLabel = json.resetAt
        ? new Date(Number(json.resetAt)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
        : 'next week';
      const limitText = json.reason === 'MINUTES_LIMIT'
        ? `your weekly audio limit (${caps.weeklyMinutes ?? ''} min)`
        : `your weekly analysis limit (${caps.weeklyAnalyses ?? ''} analyses)`;
      const quotaErr = new Error(`You've reached ${limitText}. It resets on ${resetLabel}.`);
      quotaErr.code = 'QUOTA_EXCEEDED';
      throw quotaErr;
    }

    const msg =
      errorCode === 'FILE_REQUIRED'           ? 'No file was provided.' :
      errorCode === 'INVALID_FILE_TYPE'       ? 'Unsupported file type.' :
      errorCode === 'FILE_TOO_LARGE'          ? 'File is too large. Maximum size is 100 MB. Try a shorter recording or compress the file.' :
      errorCode === 'TRANSCRIPTION_FAILED'    ? 'Transcription service is unavailable. Please try again.' :
      errorCode === 'SARVAM_KEY_MISSING'      ? 'Server configuration error (missing API key).' :
      errorCode === 'URL_REQUIRED'            ? 'Please enter a URL.' :
      errorCode === 'URL_NOT_SUPPORTED'       ? "This URL type isn't supported. Try YouTube, Google Drive, or a direct audio link." :
      errorCode === 'APYHUB_KEY_MISSING'      ? 'YouTube extraction is not configured. Please contact support.' :
      errorCode === 'APYHUB_EXTRACTION_FAILED'? "Couldn't extract audio from this YouTube video. It may be private, age-restricted, or unavailable." :
      errorCode === 'APYHUB_TIMEOUT'          ? 'YouTube extraction timed out. Try a shorter video or upload the audio directly.' :
      errorCode === 'URL_FETCH_FAILED'        ? "Couldn't download audio from this URL. Make sure it's publicly accessible." :
      errorCode === 'BATCH_TIMEOUT'           ? 'Processing timed out. Try a shorter clip or recording.' :
      'Something went wrong. Please try again.';
    throw new Error(msg);
  }

  let json;
  try {
    json = await res.json();
  } catch (parseErr) {
    console.error('[UnifiedInputHub] Failed to parse success response:', parseErr);
    throw new Error('Server returned invalid response. Please try again.');
  }

  if (!json.success) throw new Error('Server returned unsuccessful response.');
  return json;
}

export default function UnifiedInputHub({ onAnalysisComplete }) {
  const [activeTab, setActiveTab] = useState('audio');
  const [phase, setPhase] = useState('idle');
  const [loadingStage, setLoadingStage] = useState(0);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [urlInput, setUrlInput] = useState('');
  const [bgSessionId, setBgSessionId] = useState(null);
  const [bgFileSizeBytes, setBgFileSizeBytes] = useState(0);

  const submit = useCallback(async (formData, estimatedFileSize = 0) => {
    setPhase('loading');
    setLoadingStage(0);
    setErrorMsg(null);
    setBgFileSizeBytes(estimatedFileSize);
    const t1 = setTimeout(() => setLoadingStage(1), 900);
    const t2 = setTimeout(() => setLoadingStage(2), 4000);
    try {
      const data = await postFormData(formData);
      clearTimeout(t1); clearTimeout(t2);
      if (data.transcription_status === 'processing') {
        setBgSessionId(data.session_id);
        setPhase('background');
      } else {
        setResult(data);
        setPhase('done');
        onAnalysisComplete?.(data);
      }
    } catch (err) {
      clearTimeout(t1); clearTimeout(t2);
      setErrorMsg(err.message);
      setPhase('error');
    }
  }, [onAnalysisComplete]);

  const handleAudioReady = useCallback((file, duration, audioFeatures = null) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('source_type', 'recording');
    fd.append('duration', String(duration));
    if (audioFeatures) fd.append('audioFeatures', JSON.stringify(audioFeatures));
    submit(fd, file.size);
  }, [submit]);

  const handleVideoSubmit = useCallback((file, duration, audioFeatures = null) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('source_type', 'recording');
    fd.append('duration', String(duration));
    if (audioFeatures) fd.append('audioFeatures', JSON.stringify(audioFeatures));
    submit(fd, file.size);
  }, [submit]);

  const handleFileSubmit = useCallback(async (file) => {
    const duration = await probeDuration(file);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('source_type', 'upload');
    fd.append('duration', String(duration));
    submit(fd, file.size);
  }, [submit]);

  const handleUrlSubmit = useCallback((e) => {
    e.preventDefault();
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    const fd = new FormData();
    fd.append('source_type', 'url');
    fd.append('url', trimmed);
    submit(fd);
  }, [urlInput, submit]);

  const reset = () => {
    setPhase('idle');
    setResult(null);
    setErrorMsg(null);
    setUrlInput('');
    setBgSessionId(null);
    setBgFileSizeBytes(0);
  };

  const { elapsedSeconds: bgElapsedSeconds, progressPercent } = useBackgroundTranscription({
    enabled: phase === 'background' && !!bgSessionId,
    pollFn: async () => {
      const res = await catalystFetch(`${API_BASE}/sessions/${bgSessionId}`);
      if (!res.ok) {
        if (res.status === 404) return null; // still warming up — keep polling
        throw new Error(`Session fetch failed: ${res.status}`);
      }
      const data = await res.json();
      return data?.success && data.session ? data.session : null;
    },
    isComplete: (session) => session?.status === 'completed',
    isFailed: (session) => session?.status === 'failed',
    getError: (session) => {
      try {
        const m = JSON.parse(session?.metrics_json || '{}');
        return m.error_message || null;
      } catch { return null; }
    },
    onComplete: (session) => {
      const metrics = typeof session.metrics === 'string'
        ? JSON.parse(session.metrics)
        : session.metrics;
      const completed = {
        success: true,
        session_id: session.session_id,
        transcript: session.transcript || '',
        language: session.language || 'en-IN',
        metrics,
      };
      setResult(completed);
      setPhase('done');
      onAnalysisComplete?.(completed);
    },
    onFail: (msg) => {
      setErrorMsg(msg);
      setPhase('error');
      setBgSessionId(null);
    },
    estimatedFileSize: bgFileSizeBytes,
    // Scale the poll timeout with estimated audio length (50 KB/s heuristic) so
    // long recordings aren't declared timed-out while the backend batch job runs.
    timeoutMs: transcriptionTimeoutMs(bgFileSizeBytes / (50 * 1024)),
  });

  if (phase === 'done' && result) {
    return <AnalysisReport result={result} onReset={reset} />;
  }

  if (phase === 'background') {
    const minutes = Math.floor(bgElapsedSeconds / 60);
    const seconds = bgElapsedSeconds % 60;
    const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

    return (
      <div className="flex flex-col items-center justify-center py-16 gap-6 text-center">
        <div>
          <p className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>
            Transcribing in background
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Your file is large — processing in progress
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-xs">
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
          >
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${progressPercent}%`,
                background: 'linear-gradient(90deg, var(--accent), var(--accent))',
              }}
            />
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
            {progressPercent}% — {timeStr} elapsed
          </p>
        </div>

        <p className="text-xs" style={{ color: 'var(--text-faint)' }}>
          Results will appear below when ready.
        </p>

        <button onClick={reset} className="btn-bouncy text-sm px-5 py-2">
          Analyse another file
        </button>
      </div>
    );
  }

  const isLoading = phase === 'loading';

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div
        className="flex gap-1 rounded-xl p-1"
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)' }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => { if (!isLoading) setActiveTab(tab.id); }}
            disabled={isLoading}
            className="flex-1 py-2 px-1 text-xs sm:text-sm font-medium rounded-lg transition-all text-center"
            style={
              activeTab === tab.id
                ? { background: 'var(--accent-dim)', color: 'var(--accent)', border: '1px solid var(--border-accent)' }
                : { color: 'var(--text-secondary)', opacity: isLoading ? 0.5 : 1, cursor: isLoading ? 'not-allowed' : 'pointer' }
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error banner */}
      {phase === 'error' && errorMsg && (
        <div
          className="px-4 py-3 rounded-xl text-sm flex items-start justify-between gap-3"
          style={{ color: 'var(--color-danger)', background: 'var(--color-danger-dim)', border: '1px solid var(--color-danger-border)' }}
        >
          <div className="min-w-0">
            <p className="font-medium">Transcription failed</p>
            <p className="text-xs mt-1 opacity-90 break-words">{errorMsg}</p>
          </div>
          <button
            onClick={reset}
            className="font-medium shrink-0 transition-colors hover:opacity-80"
            style={{ color: 'var(--color-danger)', whiteSpace: 'nowrap' }}
          >
            Try again
          </button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <div
            className="w-10 h-10 rounded-full"
            style={{
              border: '3px solid var(--accent-dim)',
              borderTopColor: 'var(--accent)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div className="text-center">
            <p className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
              {LOADING_STAGES[loadingStage]}
            </p>
            <div className="flex gap-2 justify-center mt-2">
              {LOADING_STAGES.map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                  style={{ background: i <= loadingStage ? 'var(--accent)' : 'var(--border-accent)' }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab panels */}
      {!isLoading && (
        <div>
          {activeTab === 'audio' && (
            <LiveAudioRecorder onSubmit={handleAudioReady} isLoading={isLoading} />
          )}
          {activeTab === 'video' && (
            <LiveVideoRecorder onSubmit={handleVideoSubmit} isLoading={isLoading} />
          )}
          {activeTab === 'upload' && (
            <FileUploader onSubmit={handleFileSubmit} isLoading={isLoading} />
          )}
          {activeTab === 'url' && (
            <form onSubmit={handleUrlSubmit} className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1.5"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  YouTube, Google Drive, or Direct Audio URL
                </label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://youtube.com/watch?v=… or drive.google.com/…"
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-accent)',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  onBlur={e => { e.target.style.borderColor = 'var(--border-accent)'; }}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={!urlInput.trim()}
                className="btn-bouncy w-full py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔍 Analyze URL
              </button>
              <p className="text-xs text-center" style={{ color: 'var(--text-faint)' }}>
                YouTube, Google Drive, and direct audio links (.mp3, .wav, .m4a) are supported.
              </p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
